import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchStrategies, fetchStrategy } from '../api';

const REFRESH_INTERVAL = 5000;

export default function RollingStrangle() {
  const [strategies, setStrategies] = useState([]);
  const [selectedStrategy, setSelectedStrategy] = useState('');
  const [detail, setDetail] = useState(null);
  const navigate = useNavigate();

  const loadStrategies = useCallback(async () => {
    try {
      const res = await fetchStrategies();
      const rs = (res.data?.strategies || []).filter(s => s.entry_type === 'rolling_strangle');
      setStrategies(rs);
      if (!selectedStrategy && rs.length > 0) setSelectedStrategy(rs[0].name);
    } catch {}
  }, [selectedStrategy]);

  const loadDetail = useCallback(async () => {
    if (!selectedStrategy) return;
    try {
      const res = await fetchStrategy(selectedStrategy);
      if (res.data?.strategy) setDetail(res.data.strategy);
    } catch {}
  }, [selectedStrategy]);

  useEffect(() => { loadStrategies(); }, [loadStrategies]);
  useEffect(() => {
    loadDetail();
    const id = setInterval(loadDetail, REFRESH_INTERVAL);
    return () => clearInterval(id);
  }, [loadDetail]);

  const params = detail?.params || {};
  const positions = detail?.positions || [];
  const events = detail?.events || [];
  const pnl = detail?.pnl || 0;
  const currentStrat = strategies.find(s => s.name === selectedStrategy);

  if (strategies.length === 0) {
    return (
      <div className="rs-page">
        <div className="rs-empty">
          <div className="rs-empty-icon">🔄</div>
          <h2>Rolling Short Strangle</h2>
          <p>Auto-sell OTM CE &amp; PE with rolling adjustments that converge into a straddle.</p>
          <div className="rs-empty-steps">
            <div className="rs-step"><span className="rs-step-num">1</span><span>Go to <strong>Settings</strong></span></div>
            <div className="rs-step"><span className="rs-step-num">2</span><span>Click <strong>+ Add Strategy</strong></span></div>
            <div className="rs-step"><span className="rs-step-num">3</span><span>Select type <strong>Rolling Strangle</strong></span></div>
          </div>
          <button className="rs-btn rs-btn-primary" onClick={() => navigate('/settings')}>Go to Settings</button>
        </div>
      </div>
    );
  }

  const phase = params.phase || '—';
  const isStraddle = phase === 'STRADDLE';

  return (
    <div className="rs-page">
      {/* Header */}
      <div className="rs-header">
        <div className="rs-header-left">
          <h1>Rolling Strangle</h1>
          <span className={`rs-phase-badge ${isStraddle ? 'rs-phase-straddle' : phase === 'STRANGLE' ? 'rs-phase-strangle' : ''}`}>
            {phase}
          </span>
        </div>
        <div className="rs-header-right">
          <div className="rs-strat-selector">
            <select value={selectedStrategy} onChange={e => setSelectedStrategy(e.target.value)}>
              {strategies.map(s => (
                <option key={s.name} value={s.name}>
                  {s.account_name || 'Paper'} {s.paper_trading ? '(Paper)' : '(Live)'}
                </option>
              ))}
            </select>
            {currentStrat && (
              <span className={`rs-mode-badge ${currentStrat.paper_trading ? '' : 'rs-mode-live'}`}>
                {currentStrat.paper_trading ? 'PAPER' : 'LIVE'}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="rs-stats-row">
        <div className="rs-stat-card rs-stat-pnl">
          <span className="rs-stat-label">Total P&L</span>
          <span className={`rs-stat-value ${pnl >= 0 ? 'rs-val-pos' : 'rs-val-neg'}`}>
            {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)}
          </span>
        </div>
        <div className="rs-stat-card">
          <span className="rs-stat-label">State</span>
          <span className={`rs-stat-value rs-state-${(detail?.state || 'idle').toLowerCase()}`}>
            {detail?.state || 'IDLE'}
          </span>
        </div>
        <div className="rs-stat-card">
          <span className="rs-stat-label">Index / Spot</span>
          <span className="rs-stat-value">{params.index || '—'}</span>
        </div>
        <div className="rs-stat-card">
          <span className="rs-stat-label">Initial Spot</span>
          <span className="rs-stat-value">{params.initial_spot ? params.initial_spot.toLocaleString() : '—'}</span>
        </div>
        <div className="rs-stat-card">
          <span className="rs-stat-label">Reference</span>
          <span className="rs-stat-value">{params.reference ? params.reference.toLocaleString() : '—'}</span>
        </div>
        <div className="rs-stat-card">
          <span className="rs-stat-label">Reforms</span>
          <span className="rs-stat-value">{params.reform_count ?? 0}</span>
        </div>
      </div>

      <div className="rs-layout">
        {/* Left: Position & Straddle Info */}
        <div className="rs-left">
          {/* Straddle Info Card (when in straddle phase) */}
          {isStraddle && (
            <div className="rs-straddle-card">
              <div className="rs-straddle-head">
                <h3>Straddle @ {params.straddle_strike?.toLocaleString()}</h3>
                <span className={`rs-sl-tag ${params.is_first_straddle ? 'rs-sl-first' : 'rs-sl-reform'}`}>
                  SL {params.is_first_straddle ? '100%' : '60%'}
                </span>
              </div>
              <div className="rs-sl-bar">
                <div className="rs-sl-bound rs-sl-lower">
                  <span className="rs-sl-bound-label">Down SL</span>
                  <span className="rs-sl-bound-val">{params.straddle_lower?.toLocaleString() || '—'}</span>
                </div>
                <div className="rs-sl-center">
                  <span className="rs-sl-center-label">Strike</span>
                  <span className="rs-sl-center-val">{params.straddle_strike?.toLocaleString()}</span>
                </div>
                <div className="rs-sl-bound rs-sl-upper">
                  <span className="rs-sl-bound-label">Up SL</span>
                  <span className="rs-sl-bound-val">{params.straddle_upper?.toLocaleString() || '—'}</span>
                </div>
              </div>
              <div className="rs-sl-detail">
                SL = {params.straddle_sl?.toFixed(1)} pts
              </div>
            </div>
          )}

          {/* Strangle Info (when in strangle phase) */}
          {phase === 'STRANGLE' && (
            <div className="rs-strangle-card">
              <h3>Strangle Active</h3>
              <div className="rs-strangle-info">
                <div className="rs-strangle-stat">
                  <span className="rs-stat-label">Offset</span>
                  <span className="rs-stat-value">500 pts</span>
                </div>
                <div className="rs-strangle-stat">
                  <span className="rs-stat-label">Trigger</span>
                  <span className="rs-stat-value">300 pts</span>
                </div>
                <div className="rs-strangle-stat">
                  <span className="rs-stat-label">Reference</span>
                  <span className="rs-stat-value">{params.reference?.toLocaleString() || '—'}</span>
                </div>
              </div>
              <div className="rs-strangle-triggers">
                <div className="rs-trigger-item rs-trigger-up">
                  ▲ Roll PE if spot ≥ {params.reference ? (params.reference + 300).toLocaleString() : '—'}
                </div>
                <div className="rs-trigger-item rs-trigger-down">
                  ▼ Roll CE if spot ≤ {params.reference ? (params.reference - 300).toLocaleString() : '—'}
                </div>
              </div>
            </div>
          )}

          {/* Open Positions */}
          <div className="rs-card">
            <div className="rs-card-head">
              <h3>Open Positions</h3>
              <span className="rs-pos-count">{positions.length}</span>
            </div>
            {positions.length === 0 ? (
              <div className="rs-empty-pos">
                <span>📭</span>
                <p>No open positions</p>
              </div>
            ) : (
              <div className="rs-positions">
                {positions.map((pos, idx) => (
                  <div key={idx} className="rs-pos-card">
                    <div className="rs-pos-top">
                      <span className="rs-badge rs-badge-sell">SELL</span>
                      <span className={`rs-badge ${pos.option_type === 'CE' ? 'rs-badge-ce' : 'rs-badge-pe'}`}>
                        {pos.option_type}
                      </span>
                      <span className="rs-pos-strike">{pos.strike?.toLocaleString()}</span>
                      <span className="rs-pos-lots">×{pos.lots}</span>
                      <span className={`rs-pos-pnl ${pos.pnl >= 0 ? 'rs-val-pos' : 'rs-val-neg'}`}>
                        {pos.pnl >= 0 ? '+' : ''}{pos.pnl?.toFixed(2)}
                      </span>
                    </div>
                    <div className="rs-pos-bottom">
                      <span>Entry <strong>{pos.entry_price?.toFixed(2) || '—'}</strong></span>
                      <span className="rs-pos-arrow">→</span>
                      <span>LTP <strong>{pos.current_price?.toFixed(2) || '—'}</strong></span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Premium History */}
          <div className="rs-card">
            <div className="rs-card-head">
              <h3>Collected Premiums</h3>
              <span className="rs-premium-total">
                Σ {params.total_premium_collected?.toFixed(1) || '0'}
              </span>
            </div>
            <div className="rs-premiums">
              {(params.stored_premiums || []).length === 0 ? (
                <p className="rs-muted">No premiums collected yet</p>
              ) : (
                <div className="rs-premium-list">
                  {(params.stored_premiums || []).map((p, i) => (
                    <span key={i} className="rs-premium-chip">₹{p.toFixed(1)}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: Event Log */}
        <div className="rs-right">
          <div className="rs-card rs-card-events">
            <div className="rs-card-head">
              <h3>Event Log</h3>
              <span className="rs-event-count">{events.length}</span>
            </div>
            <div className="rs-events">
              {events.length === 0 ? (
                <p className="rs-muted">No events yet</p>
              ) : (
                events.slice().reverse().map((evt, idx) => (
                  <div key={idx} className="rs-event">
                    <span className="rs-evt-time">{evt.time}</span>
                    <span className={`rs-evt-type rs-evt-${evt.type?.toLowerCase().replace(/_/g, '')}`}>{evt.type}</span>
                    <span className="rs-evt-msg">{evt.message}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Closed Trades Summary */}
          <div className="rs-card">
            <div className="rs-card-head">
              <h3>Summary</h3>
            </div>
            <div className="rs-summary-grid">
              <div className="rs-summary-item">
                <span className="rs-stat-label">Closed Trades</span>
                <span className="rs-stat-value">{params.closed_trades ?? 0}</span>
              </div>
              <div className="rs-summary-item">
                <span className="rs-stat-label">Total Premium</span>
                <span className="rs-stat-value">{params.total_premium_collected?.toFixed(1) || '0'}</span>
              </div>
              <div className="rs-summary-item">
                <span className="rs-stat-label">Reforms</span>
                <span className="rs-stat-value">{params.reform_count ?? 0}</span>
              </div>
              <div className="rs-summary-item">
                <span className="rs-stat-label">Exit Time</span>
                <span className="rs-stat-value">{params.exit_time || '—'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
